import { useState, useEffect } from 'react';
import { Star, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokerComparison = () => {
  const [brokers, setBrokers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${API}/brokers`);
      setBrokers(response.data);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <section className="py-24 md:py-32" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mb-6"
              style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
              data-testid="comparison-heading"
            >
              Broker Comparison
            </h1>
            <p className="text-base leading-relaxed" style={{ color: '#475569' }}>
              Compare India's top brokers and make an informed decision for your trading journey.
              Note: Angel One offers different plans suitable for your trading style. The brokerage shown below is for one of the plans. There are plans where you also get free advisory along with trading.
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-base" style={{ color: '#475569' }}>
                Loading comparison data...
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                <div className="bg-[#FFFFFF] border border-[rgba(10,15,26,0.05)]">
                  <div
                    className="grid grid-cols-7 gap-4 p-4"
                    style={{ backgroundColor: '#0A0F1A', color: 'white' }}
                  >
                    <div className="font-semibold" style={{ fontFamily: 'Outfit' }}>Broker</div>
                    <div className="font-semibold" style={{ fontFamily: 'Outfit' }}>Account Opening</div>
                    <div className="font-semibold" style={{ fontFamily: 'Outfit' }}>Annual Charges</div>
                    <div className="font-semibold" style={{ fontFamily: 'Outfit' }}>Equity Delivery</div>
                    <div className="font-semibold" style={{ fontFamily: 'Outfit' }}>Equity Intraday</div>
                    <div className="font-semibold" style={{ fontFamily: 'Outfit' }}>Demat Charges</div>
                    <div className="font-semibold" style={{ fontFamily: 'Outfit' }}>Rating</div>
                  </div>

                  {brokers.map((broker, idx) => (
                    <div
                      key={broker.id}
                      data-testid={`broker-row-${idx}`}
                      className={`grid grid-cols-7 gap-4 p-4 border-t border-[#E2E8F0] ${
                        broker.is_recommended ? 'bg-[#FFF4E6]' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: '#0A0F1A' }}>
                          {broker.name}
                        </span>
                        {broker.is_recommended && (
                          <CheckCircle size={16} style={{ color: '#10B981' }} />
                        )}
                      </div>
                      <div style={{ color: '#475569' }}>{broker.account_opening_fee}</div>
                      <div style={{ color: '#475569' }}>{broker.annual_maintenance}</div>
                      <div style={{ color: '#475569' }}>{broker.equity_delivery}</div>
                      <div style={{ color: '#475569' }}>{broker.equity_intraday}</div>
                      <div style={{ color: '#475569' }}>{broker.demat_charges}</div>
                      <div className="flex items-center gap-1">
                        <Star size={16} fill="#FF5722" stroke="#FF5722" />
                        <span style={{ color: '#0A0F1A' }}>{broker.rating}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 p-6 bg-[#FFF4E6] border-l-4" style={{ borderColor: '#FF5722' }}>
            <div className="flex items-start gap-3">
              <CheckCircle size={24} style={{ color: '#10B981', flexShrink: 0 }} />
              <div>
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
                >
                  Our Recommendation: Angel One
                </h3>
                <p className="text-base" style={{ color: '#475569' }}>
                  Angel One offers multiple plans suited to different trading styles — from zero-brokerage
                  delivery plans to premium plans with free advisory and research. The charges shown above
                  represent one of the available plans. Contact us to find the best plan for your needs!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-4"
              style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
            >
              Why Angel One Stands Out
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-hover bg-[#FAFAFA] border border-[rgba(10,15,26,0.05)] p-8 text-center">
              <h3
                className="text-xl font-semibold mb-3"
                style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
              >
                Multiple Plans
              </h3>
              <p className="text-base" style={{ color: '#475569' }}>
                Choose from different plans suited to your trading style — including plans with free advisory
              </p>
            </div>
            <div className="card-hover bg-[#FAFAFA] border border-[rgba(10,15,26,0.05)] p-8 text-center">
              <h3
                className="text-xl font-semibold mb-3"
                style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
              >
                Advanced Technology
              </h3>
              <p className="text-base" style={{ color: '#475569' }}>
                State-of-the-art trading platforms with lightning-fast execution
              </p>
            </div>
            <div className="card-hover bg-[#FAFAFA] border border-[rgba(10,15,26,0.05)] p-8 text-center">
              <h3
                className="text-xl font-semibold mb-3"
                style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
              >
                Free Advisory
              </h3>
              <p className="text-base" style={{ color: '#475569' }}>
                Premium plans include free expert advisory and research to guide your investments
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/brokerage-plans"
              data-testid="comparison-cta-button"
              className="btn-primary inline-flex items-center gap-2"
              style={{
                backgroundColor: '#FF5722',
                color: 'white',
                fontWeight: 600,
                padding: '1rem 2rem',
                textDecoration: 'none',
              }}
            >
              Open Angel One Account
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrokerComparison;